import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';
import { Row, Col } from 'react-bootstrap';
import { List } from 'immutable';

import config from '../../config';
import { GlobalModals } from '../../constants/AppConstants';
import { BadRequestError } from '../../errors/API';
import t from '../../lib/I18n';
import connectToStores from '../../helpers/connectToStores';

import CustomerService from '../../services/CustomerService';
import PostcodeLookUp from '../../records/customer/PostcodeLookUp';
import ModalActions from '../../actions/ModalActions';
import FeatureFlagStore from '../../stores/FeatureFlagStore';

import OnEnterTrigger from '../general/OnEnterTrigger';
import Icon from '../general/Icon';
import ValidatedFormField from '../form/ValidatedField';
import Select from '../form/Select';
import FormError from '../form/Error';

function _getStateFromStores() {
    const enablePostCodeLookUp = FeatureFlagStore.get('enablePostCodeLookUp');
    return { enablePostCodeLookUp };
}

export class PostCodeLookUp extends React.PureComponent {
    static propTypes = {
        country: PropTypes.string.isRequired,
        onSelectAddress: PropTypes.func.isRequired,
        onFoundAddressList: PropTypes.func,
        cols: PropTypes.object,
        disabled: PropTypes.bool,
        enablePostCodeLookUp: PropTypes.bool,
        outsideLabel: PropTypes.bool

    };

    static defaultProps = {
        cols: { xs: 12 },
        disabled: false,
        enablePostCodeLookUp: true,
        onFoundAddressList: _.noop
    };

    state = {
        lookUpForm: new PostcodeLookUp(),
        loading: false,
        addressOptions: null,
        showInvalidError: false,
        showNotFoundError: false
    };

    handlePostCodeChange = (field, value) => {
        const updatedForm = this.state.lookUpForm.set(field, value);
        this.setState({ lookUpForm: updatedForm, showInvalidError: false, showNotFoundError: false, addressOptions: null });
    };

    handleSubmit = (e) => {
        e.preventDefault();
        this.lookUpPostCode();
    }

    lookUpPostCode = async () => {
        const { onFoundAddressList } = this.props;
        const { lookUpForm } = this.state;
        if (lookUpForm.get('postcode') && !lookUpForm.getValidationSummary().postcode) {
            this.setState({ loading: true });
            let addressOptions = new List();
            try {
                addressOptions = await CustomerService.lookUpAddress(lookUpForm.get('postcode'));
                if (!addressOptions.size) {
                    this.setState({ showNotFoundError: true });
                } else {
                    onFoundAddressList();
                }
            } catch (e) {
                if (e instanceof BadRequestError) {
                    this.setState({ showInvalidError: true });
                } else {
                    console.error(e);
                    ModalActions.open(GlobalModals.ERROR);
                }
            } finally {
                this.setState({ addressOptions, loading: false });
            }
        }
    };

    handleSelectAddress = (e) => {
        const selectedAddress = this.state.addressOptions.find(option => option.toString() === e.target.value);
        const updatedAddress = selectedAddress.merge({
            line1: selectedAddress.get('line1') || '',
            line2: selectedAddress.get('line2') || '',
            line3: selectedAddress.get('line3') || '',
            town: selectedAddress.get('town') || ''
        });
        this.props.onSelectAddress(updatedAddress);
    }

    render() {
        if (!this.props.enablePostCodeLookUp) {
            return null;
        }

        const { country, cols, disabled, outsideLabel } = this.props;

        if (country !== config.get('defaultDeliveryCountry')) {
            return null;
        }

        const { addressOptions, loading, lookUpForm, showInvalidError, showNotFoundError } = this.state;
        const disableSearch = !lookUpForm.get('postcode') || !!lookUpForm.getValidationSummary().postcode || loading;

        return (
            <div className="postcode-lookup-with-select">
                <Row>
                    <Col {...cols} md={ cols.md || 6 }>
                        <div className={classnames("postcode-lookup form-group", { "postcode-lookup--outside-label": outsideLabel })}>
                            <div className="input-group">
                                <form onSubmit={this.handleSubmit}>
                                    <ValidatedFormField
                                        className={classnames("bold-placeholder", { "with-addon": !outsideLabel })}
                                        form={this.state.lookUpForm}
                                        field="postcode"
                                        type="text"
                                        id="postCodeLookUp"
                                        value={this.state.lookUpForm.get('postcode')}
                                        disabled={disabled}
                                        label={t('postcode-lookup.input.placeholder')}
                                        placeholder={t('postcode-lookup.input.placeholder')}
                                        onChange={this.handlePostCodeChange}
                                        noFloatyPlaceholder
                                        externalFormGroup
                                        hideOptional={outsideLabel}
                                        showRequired={outsideLabel}
                                        outsideLabel={outsideLabel}
                                        />
                                </form>
                                <div className={classnames({ "input-group-addon": !outsideLabel, "input-group-addon--outside-label": outsideLabel })}>
                                    <OnEnterTrigger className="postcode-search-button"
                                        element="span"
                                        role="button"
                                        onClick={this.lookUpPostCode}
                                        disabled={disableSearch}
                                        aria-label={t('postcode-lookup.button.label')}
                                    >
                                        {loading ? <Icon icon="spinner" className="otrl-spin" />
                                            : <Icon icon="search" />}
                                    </OnEnterTrigger>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col {...cols} md={ outsideLabel ? 12 : cols.md || 6 }>
                        {addressOptions && !!addressOptions.size &&
                            <div className={classnames("select-container otrl-postal-address-select",
                                { "postal-address-select--outside-label": outsideLabel })}>
                                <Select type="select"
                                    onChange={this.handleSelectAddress}
                                    defaultValue=""
                                    label={t('postcode-lookup.select.placeholder')}
                                    labelClassName="sr-only">
                                    <option value="" disabled>{t('postcode-lookup.select.placeholder')}</option>
                                    {addressOptions.map((option, index) =>
                                        <option value={option.toString()} key={index}>
                                            {option.toString()}
                                        </option>)}
                                </Select>
                            </div>}

                        {addressOptions && !addressOptions.size && showInvalidError &&
                            <FormError className="postcode-lookup__error"
                                message={t('postcode-lookup.error.invalid')} />}
                        {addressOptions && !addressOptions.size && showNotFoundError &&
                            <FormError className="postcode-lookup__error"
                                message={t('postcode-lookup.error.not-found')} />}
                    </Col>
                </Row>
            </div>
        );
    }
}
export default connectToStores([FeatureFlagStore], _getStateFromStores)(PostCodeLookUp);



// WEBPACK FOOTER //
// ./src/components/customer/PostCodeLookUp.js